import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import SmartUpload from "./SmartUpload";
import {
  Palette, Upload, FileText, Image, Video, Presentation,
  ChevronDown, ChevronUp, Plus, Trash2, Check, Loader2,
  Sparkles, Eye, Edit3, X, File, BookOpen, Briefcase, Target
} from "lucide-react";

const ASSET_CATEGORIES = [
  { id: "logo", label: "Logo", icon: Image, accept: "image/*", multiple: false },
  { id: "brandGuides", label: "Brand Guidelines", icon: BookOpen, accept: ".pdf,.doc,.docx", multiple: true },
  { id: "designDocs", label: "Design Docs", icon: Palette, accept: ".pdf,.ai,.psd,.sketch,.fig", multiple: true },
  { id: "presentations", label: "Presentations", icon: Presentation, accept: ".pptx,.pdf,.key", multiple: true },
  { id: "skillDocs", label: "Skills & Capabilities", icon: Briefcase, accept: ".pdf,.doc,.docx,.txt", multiple: true },
  { id: "strategyDocs", label: "Strategy Documents", icon: Target, accept: ".pdf,.doc,.docx,.txt", multiple: true },
  { id: "other", label: "Other Assets", icon: File, accept: "*", multiple: true }
];

const BrandManager = ({ userId, currentInstance }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSmartUpload, setShowSmartUpload] = useState(false);
  const [brandAssets, setBrandAssets] = useState({
    logo: null,
    colors: [],
    brandVoice: "",
    missionStatement: "",
    brandGuides: [],
    designDocs: [],
    presentations: [],
    skillDocs: [],
    strategyDocs: [],
    other: []
  });
  const [uploading, setUploading] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [newColor, setNewColor] = useState("#00A38A");
  const [expandedCategory, setExpandedCategory] = useState(null);

  // Get effective userId (instance-specific or global)
  const effectiveKey = currentInstance?.instanceId 
    ? `${userId}_${currentInstance.instanceId}` 
    : userId;

  const loadBrandAssets = useCallback(async () => {
    setLoading(true);
    try {
      // Load workspace-specific brand assets
      const endpoint = currentInstance?.instanceId 
        ? `${API}/brand/${userId}?instanceId=${currentInstance.instanceId}`
        : `${API}/brand/${userId}`;
      const response = await axios.get(endpoint);
      if (response.data) {
        setBrandAssets(prev => ({ 
          ...prev, 
          ...response.data,
          brandGuides: response.data.brandGuides || [],
          designDocs: response.data.designDocs || [],
          skillDocs: response.data.skillDocs || [],
          strategyDocs: response.data.strategyDocs || [],
          other: response.data.other || []
        }));
      }
    } catch (error) {
      console.error("Error loading brand assets:", error);
    } finally {
      setLoading(false);
    }
  }, [userId, currentInstance?.instanceId]);

  useEffect(() => {
    if (userId && isExpanded) {
      loadBrandAssets();
    }
  }, [userId, isExpanded, loadBrandAssets]);

  const saveBrandAssets = async () => {
    setLoading(true);
    try {
      const endpoint = currentInstance?.instanceId 
        ? `${API}/brand/${userId}?instanceId=${currentInstance.instanceId}`
        : `${API}/brand/${userId}`;
      await axios.put(endpoint, brandAssets);
      toast.success("Brand assets saved successfully");
      setEditMode(false);
    } catch (error) {
      toast.error("Failed to save brand assets");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, categoryId) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(categoryId);
    
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", categoryId);
      formData.append("userId", userId);
      if (currentInstance?.instanceId) {
        formData.append("instanceId", currentInstance.instanceId);
      }

      try {
        const response = await axios.post(`${API}/brand/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        
        if (categoryId === "logo") {
          setBrandAssets(prev => ({ ...prev, logo: response.data.url }));
        } else {
          setBrandAssets(prev => ({
            ...prev,
            [categoryId]: [...(prev[categoryId] || []), { 
              name: file.name, 
              url: response.data.url,
              type: file.type,
              size: file.size,
              uploadedAt: new Date().toISOString()
            }]
          }));
        }
        
        toast.success(`${file.name} uploaded successfully`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploading(null);
    e.target.value = '';
  };

  const removeAsset = (categoryId, index) => {
    setBrandAssets(prev => ({
      ...prev,
      [categoryId]: prev[categoryId].filter((_, i) => i !== index)
    }));
  };

  const addColor = () => {
    if (newColor && !brandAssets.colors.includes(newColor)) {
      setBrandAssets(prev => ({
        ...prev,
        colors: [...prev.colors, newColor]
      }));
      setNewColor("#00A38A");
    }
  };

  const removeColor = (colorToRemove) => {
    setBrandAssets(prev => ({
      ...prev,
      colors: prev.colors.filter(c => c !== colorToRemove)
    }));
  };

  const totalAssets = Object.keys(brandAssets).reduce((count, key) => {
    if (Array.isArray(brandAssets[key])) return count + brandAssets[key].length;
    if (key === 'logo' && brandAssets.logo) return count + 1;
    return count;
  }, 0);

  return (
    <div className="glass-card rounded-xl overflow-hidden" data-testid="brand-manager">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-3 flex items-center justify-between hover:bg-white/5 transition-colors"
        data-testid="brand-manager-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-ism-teal/20 flex items-center justify-center">
            <Palette className="h-4 w-4 text-ism-teal" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-medium text-white">Brand Manager</h3>
            <p className="text-xs text-gray-500">
              {currentInstance?.name ? `${currentInstance.name} assets` : 'Logo, colors, voice & assets'}
              {totalAssets > 0 && <span className="text-ism-teal ml-1">({totalAssets} files)</span>}
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-white/10"
          >
            <ScrollArea className="max-h-[350px]">
              <div className="p-3 space-y-3">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-ism-teal" />
                  </div>
                ) : (
                  <>
                    {/* Workspace Notice */}
                    {currentInstance?.name && (
                      <div className="p-2 rounded-lg bg-ism-teal/10 border border-ism-teal/20 mb-3">
                        <p className="text-xs text-ism-teal">
                          Assets for <strong>{currentInstance.name}</strong> workspace
                        </p>
                      </div>
                    )}

                    {/* Smart Import Button */}
                    <Button
                      onClick={() => setShowSmartUpload(true)}
                      variant="outline"
                      className="w-full border-dashed border-ism-teal/30 text-ism-teal hover:bg-ism-teal/10 mb-3"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Smart Import (AI-powered)
                    </Button>

                    {/* Logo Section */}
                    <div className="space-y-2">
                      <label className="text-xs text-gray-400 uppercase tracking-wider">Logo</label>
                      <div className="flex items-center gap-3">
                        {brandAssets.logo ? (
                          <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center overflow-hidden">
                            <img src={brandAssets.logo} alt="Logo" className="max-w-full max-h-full object-contain" />
                          </div>
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-white/5 border border-dashed border-white/20 flex items-center justify-center">
                            <Image className="h-5 w-5 text-gray-500" />
                          </div>
                        )}
                        <label className="cursor-pointer flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileUpload(e, "logo")}
                            disabled={uploading === "logo"}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-ism-teal/30 text-ism-teal hover:bg-ism-teal/10 w-full"
                            disabled={uploading === "logo"}
                            asChild
                          >
                            <span>
                              {uploading === "logo" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
                              Upload Logo
                            </span>
                          </Button>
                        </label>
                      </div>
                    </div>

                    {/* Brand Colors */}
                    <div className="space-y-2">
                      <label className="text-xs text-gray-400 uppercase tracking-wider">Brand Colors</label>
                      <div className="flex flex-wrap gap-2">
                        {brandAssets.colors.map((color, idx) => (
                          <div
                            key={color}
                            className="group relative w-7 h-7 rounded-lg border border-white/20 cursor-pointer"
                            style={{ backgroundColor: color }}
                            title={color}
                            onClick={() => editMode && removeColor(color)}
                          >
                            {editMode && (
                              <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <X className="h-3 w-3 text-white" />
                              </div>
                            )}
                          </div>
                        ))}
                        {editMode && (
                          <div className="flex items-center gap-1">
                            <input
                              type="color"
                              value={newColor}
                              onChange={(e) => setNewColor(e.target.value)}
                              className="w-7 h-7 rounded-lg cursor-pointer"
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={addColor}
                            >
                              <Plus className="h-4 w-4 text-ism-teal" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Brand Voice & Mission - Collapsed */}
                    <div className="space-y-2">
                      <button
                        onClick={() => setExpandedCategory(expandedCategory === 'voice' ? null : 'voice')}
                        className="flex items-center justify-between w-full text-left"
                      >
                        <label className="text-xs text-gray-400 uppercase tracking-wider">Brand Voice & Mission</label>
                        <ChevronDown className={`h-3 w-3 text-gray-500 transition-transform ${expandedCategory === 'voice' ? 'rotate-180' : ''}`} />
                      </button>
                      {expandedCategory === 'voice' && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          className="space-y-2"
                        >
                          {editMode ? (
                            <>
                              <Textarea
                                value={brandAssets.brandVoice}
                                onChange={(e) => setBrandAssets(prev => ({ ...prev, brandVoice: e.target.value }))}
                                placeholder="Describe your brand's tone and voice..."
                                className="bg-white/5 border-white/10 text-white text-xs min-h-[50px]"
                              />
                              <Textarea
                                value={brandAssets.missionStatement}
                                onChange={(e) => setBrandAssets(prev => ({ ...prev, missionStatement: e.target.value }))}
                                placeholder="Your mission statement..."
                                className="bg-white/5 border-white/10 text-white text-xs min-h-[50px]"
                              />
                            </>
                          ) : (
                            <div className="space-y-2">
                              <p className="text-xs text-gray-300 bg-white/5 rounded-lg p-2">
                                <span className="text-gray-500">Voice:</span> {brandAssets.brandVoice || "Not defined"}
                              </p>
                              <p className="text-xs text-gray-300 bg-white/5 rounded-lg p-2">
                                <span className="text-gray-500">Mission:</span> {brandAssets.missionStatement || "Not defined"}
                              </p>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </div>

                    {/* Document Categories */}
                    {ASSET_CATEGORIES.slice(1).map((category) => {
                      const CategoryIcon = category.icon;
                      const assets = brandAssets[category.id] || [];
                      
                      return (
                        <div key={category.id} className="space-y-1">
                          <button
                            onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                            className="flex items-center justify-between w-full text-left py-1"
                          >
                            <div className="flex items-center gap-2">
                              <CategoryIcon className="h-3 w-3 text-gray-500" />
                              <span className="text-xs text-gray-400">{category.label}</span>
                              {assets.length > 0 && (
                                <span className="text-xs text-ism-teal">({assets.length})</span>
                              )}
                            </div>
                            <ChevronDown className={`h-3 w-3 text-gray-500 transition-transform ${expandedCategory === category.id ? 'rotate-180' : ''}`} />
                          </button>
                          
                          <AnimatePresence>
                            {expandedCategory === category.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="pl-5 space-y-1"
                              >
                                {assets.map((asset, idx) => (
                                  <div key={asset.url || asset.name || idx} className="flex items-center gap-2 bg-white/5 rounded-lg p-2 group">
                                    <FileText className="h-3 w-3 text-ism-teal flex-shrink-0" />
                                    <span className="text-xs text-gray-300 flex-1 truncate">{asset.name}</span>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <a href={asset.url} target="_blank" rel="noopener noreferrer">
                                        <Eye className="h-3 w-3 text-gray-400 hover:text-white" />
                                      </a>
                                      {editMode && (
                                        <button onClick={() => removeAsset(category.id, idx)}>
                                          <Trash2 className="h-3 w-3 text-red-400 hover:text-red-300" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                <label className="cursor-pointer block">
                                  <input
                                    type="file"
                                    accept={category.accept}
                                    multiple={category.multiple}
                                    className="hidden"
                                    onChange={(e) => handleFileUpload(e, category.id)}
                                    disabled={uploading === category.id}
                                  />
                                  <div className="flex items-center gap-2 text-xs text-ism-teal hover:text-ism-teal-light cursor-pointer p-2">
                                    {uploading === category.id ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Plus className="h-3 w-3" />
                                    )}
                                    Add {category.label.toLowerCase()}
                                  </div>
                                </label>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2 border-t border-white/10">
                      {editMode ? (
                        <>
                          <Button
                            size="sm"
                            onClick={saveBrandAssets}
                            className="bg-ism-teal hover:bg-ism-teal-light text-white flex-1 h-8"
                            disabled={loading}
                          >
                            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />}
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditMode(false)}
                            className="border-gray-600 text-gray-400 h-8"
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditMode(true)}
                          className="border-ism-teal/30 text-ism-teal hover:bg-ism-teal/10 w-full h-8"
                        >
                          <Edit3 className="h-3 w-3 mr-1" />
                          Edit Assets
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Smart Upload Modal */}
      <SmartUpload
        isOpen={showSmartUpload}
        onClose={() => setShowSmartUpload(false)}
        onComplete={(result) => {
          // Refresh brand assets if instance was updated
          if (result.instanceUpdated) {
            loadBrandAssets();
          }
          setShowSmartUpload(false);
        }}
        userId={userId}
        instanceId={currentInstance?.instanceId}
        applyToInstance={true}
      />
    </div>
  );
};

export default BrandManager;
